import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Search, FileEdit, Building2, CheckCircle2, Ban, ChevronRight, ShoppingBasket } from 'lucide-react';
import MaterialSheetEditor from './MaterialSheetEditor';
import { MS_API, money, StatusPill } from './materialSheetShared';

const fmtD = (d) => d ? new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

const TABS = [
  { id: 'draft', label: 'Drafts', icon: FileEdit, filter: (s) => s.status === 'draft' },
  { id: 'needs_wo', label: 'Needs WO', icon: Building2, filter: (s) => s.status === 'issued' && s.needs_wo },
  { id: 'issued', label: 'Issued', icon: CheckCircle2, filter: (s) => s.status === 'issued' && !s.needs_wo },
  { id: 'void', label: 'Void', icon: Ban, filter: (s) => s.status === 'void' },
];

export default function MaterialSheets() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sheets, setSheets] = useState([]);
  const [counts, setCounts] = useState({});
  const [canManage, setCanManage] = useState(false);
  const [tab, setTab] = useState('draft');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);
  const [newWo, setNewWo] = useState('');
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(MS_API);
      setSheets(r.data.sheets || []); setCounts(r.data.counts || {}); setCanManage(!!r.data.can_manage);
    } catch { toast.error('Could not load material sheets'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const id = new URLSearchParams(location.search).get('sheet');
    if (!id) { setOpen(null); return; }
    axios.get(`${MS_API}/${id}`).then(r => setOpen(r.data)).catch(() => toast.error('Could not open that sheet'));
  }, [location.search]);

  const openSheet = (s) => navigate(`/material-sheets?sheet=${s.id}`);
  const closeSheet = () => { setOpen(null); navigate('/material-sheets', { replace: true }); load(); };

  const create = async (noWo) => {
    setCreating(true);
    try {
      const r = await axios.post(MS_API, { work_order_number: noWo ? '' : newWo.trim() });
      toast.success(`${r.data.number} started${r.data.work_order_number ? ` for WO ${r.data.work_order_number}` : ' — no WO, hand it to the office when issued'}`);
      setNewWo(''); navigate(`/material-sheets?sheet=${r.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start a sheet'); }
    setCreating(false);
  };

  if (open) return <div className="space-y-4" data-testid="material-sheets"><MaterialSheetEditor sheet={open} canManage={canManage} onBack={closeSheet} onChanged={setOpen} /></div>;

  const tabCounts = { draft: counts.draft || 0, needs_wo: counts.needs_wo || 0, issued: (counts.issued || 0) - (counts.needs_wo || 0), void: counts.void || 0 };
  const ql = q.trim().toLowerCase();
  const shown = sheets.filter(TABS.find(t => t.id === tab).filter)
    .filter(s => !ql || [s.number, s.work_order_number, s.customer, s.job_title, s.pm_name, s.created_by?.name, ...(s.lines || []).map(l => l.name)].join(' ').toLowerCase().includes(ql));

  return (
    <div className="space-y-4" data-testid="material-sheets">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Material Sheets</h1>
          <p className="text-sm text-slate-500 mt-0.5">Costed pick lists from the master consumables list. Issue to a WO and the lines land on the job as pending consumables.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="ms-new">
          <input value={newWo} onChange={e => setNewWo(e.target.value)} onKeyDown={e => e.key === 'Enter' && newWo.trim() && create(false)} placeholder="WO # e.g. 26-7041"
            className="px-3 py-2.5 rounded-xl bg-white text-slate-900 text-sm w-40 outline-none focus:ring-2 focus:ring-emerald-400" data-testid="ms-new-wo" />
          <button onClick={() => create(false)} disabled={creating || !newWo.trim()} className="px-4 py-2.5 rounded-xl bg-emerald-600 disabled:bg-slate-300 text-white font-bold text-sm flex items-center gap-1.5" data-testid="ms-new-btn"><Plus className="w-4 h-4" /> New sheet</button>
          <button onClick={() => create(true)} disabled={creating} className="px-3 py-2.5 rounded-xl bg-slate-900/5 hover:bg-slate-900/10 text-slate-900 text-sm font-semibold flex items-center gap-1.5" title="Counter sale — no WO yet, the office will assign one" data-testid="ms-new-nowo-btn"><ShoppingBasket className="w-4 h-4" /> No WO</button>
        </div>
      </div>

      <div className="flex gap-1 bg-white p-1 rounded-xl border border-slate-200 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`ms-tab-${t.id}`}
            className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap flex-shrink-0 transition-all ${tab === t.id ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}>
            <t.icon className="w-4 h-4" /> {t.label}
            <span className={`ml-1 text-[11px] px-1.5 py-0.5 rounded-full ${tab === t.id ? 'bg-slate-900/10' : t.id === 'needs_wo' && tabCounts.needs_wo > 0 ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-700'}`} data-testid={`ms-count-${t.id}`}>{tabCounts[t.id]}</span>
          </button>
        ))}
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Find a sheet — number, WO, customer, item…" className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white/95 text-slate-900 text-sm outline-none focus:ring-2 focus:ring-blue-400" data-testid="ms-search" />
      </div>

      {loading && <p className="text-slate-700 text-sm">Loading…</p>}
      {!loading && shown.length === 0 && (
        <div className="bg-slate-900/5 rounded-2xl p-8 text-center text-slate-600" data-testid="ms-empty">
          {tab === 'draft' ? 'No drafts. Type a WO # (or hit "No WO" for a counter sale) to start one.' :
           tab === 'needs_wo' ? 'Nothing waiting on the office — every issued sheet has a work order.' :
           tab === 'issued' ? 'No issued sheets yet.' : 'No voided sheets.'}
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="ms-list">
        {shown.map(s => (
          <button key={s.id} onClick={() => openSheet(s)} className="text-left bg-white rounded-2xl shadow hover:shadow-lg transition-shadow p-4 flex flex-col gap-2 min-h-[110px]" data-testid={`ms-card-${s.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-mono text-lg font-extrabold text-blue-700 leading-none">{s.work_order_number || <span className="text-amber-600">No WO</span>}</p>
                <p className="font-semibold text-slate-900 truncate mt-1">{s.customer || (s.work_order_number ? '—' : 'Counter sale')}</p>
                {s.job_title && <p className="text-xs text-slate-500 truncate">{s.job_title}</p>}
              </div>
              <ChevronRight className="w-5 h-5 text-slate-500 flex-shrink-0" />
            </div>
            <div className="flex items-center gap-2 flex-wrap text-xs text-slate-600 mt-auto">
              <span className="font-mono text-slate-500">{s.number}</span>
              <span>{fmtD(s.date)}</span>
              <StatusPill s={s} />
              <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-800 font-semibold">{s.line_count} line{s.line_count === 1 ? '' : 's'}</span>
              <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 font-bold" data-testid={`ms-card-total-${s.id}`}>{money(s.subtotal)}</span>
            </div>
            <p className="text-xs text-slate-500 truncate">{s.created_by?.name}{s.pm_name ? ` · PM ${s.pm_name}` : ''}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
